// frontend/src/components/orcamento/AbaSimulacao.js
// Distribuição do RESULTADO do ano (receitas − despesas do mesmo cenário, vetor
// vindo do backend e deslocado pelas edições), chance de prejuízo, chance de bater
// uma meta, três cenários, ranking de variância (receitas e despesas) e backtest.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { parseBR, categoriasDe } from './useProjecao';
import CenarioPilares from './CenarioPilares';

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
// moeda com sinal explícito (− prejuízo), não só a cor
const fmt = (v) => `${v < 0 ? '−' : ''}${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(Math.abs(v || 0))}`;
const pctOuTraco = (x, casas = 1) => (x === null || x === undefined) ? '—' : `${(x * 100).toFixed(casas)}%`;
const pct0 = (x) => `${Math.round(x * 100)}%`;

// 3105973 → 'R$ 3,11 mi'; −450000 → '−R$ 0,45 mi' (rótulos do eixo e das linhas de referência)
const fmtMi = (v) => `${v < 0 ? '−' : ''}R$ ${(Math.abs(v) / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mi`;
const COR_FAIXA = '#00205b';   // barras dentro da faixa provável (P10–P90)
const COR_CAUDA = '#b9c4d8';   // mesmo matiz, mais claro: cenários fora da faixa ou com prejuízo
const INK = '#3f4a5c';
const ZERO = '#1b1f2a';        // linha do zero (divisa prejuízo | lucro)

// Dica do histograma em HTML próprio: o tooltip padrão herda o azul-marinho da página
function DicaHistograma({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  const pct = total ? ((p.n / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '0';
  return (
    <div className="op-dica">
      <div className="op-dica-titulo">{fmtMi(p.x0)} a {fmtMi(p.x1)}</div>
      <div><b>{p.n}</b> cenários ({pct}%){p.x1 <= 0 ? ' · prejuízo' : ''}</div>
    </div>
  );
}

// barra com 2px de respiro entre vizinhas e topo arredondado
function BarraHistograma({ x, y, width, height, fill }) {
  if (!(height > 0)) return null;
  const w = Math.max(width - 2, 1);
  const r = Math.min(4, w / 2, height);
  const x0 = x + 1;
  return (
    <path fill={fill} d={`M${x0},${y + height} V${y + r} Q${x0},${y} ${x0 + r},${y} H${x0 + w - r} Q${x0 + w},${y} ${x0 + w},${y + r} V${y + height} Z`} />
  );
}

// rótulo das linhas de referência, no topo, em tinta de texto (não na cor da linha)
const rotuloRef = (titulo, valor, peso = 600) => ({
  position: 'top', value: `${titulo} ${fmtMi(valor)}`, fill: INK, fontSize: 12, fontWeight: peso,
});

// σ aproximado de uma categoria no ano: Σ_mes (p90 − p10) / 2,56
const sigmaAno = (c) => (c.meses || []).reduce((s, m) => s + ((m.p90 || 0) - (m.p10 || 0)) / 2.56, 0);

// linha "rótulo ........ valor" dos cards
const Linha = ({ rotulo, valor }) => (
  <div className="d-flex justify-content-between py-1"><span>{rotulo}</span><b>{valor}</b></div>
);

export default function AbaSimulacao({ clienteId, proj, resultado, receitas, despesas, probPrejuizo, chanceMeta, deslocamento, pilaresSimulados, composicaoPilar, onAbrirCategoria, consolidar = false, siglas = null }) {
  const grupo = !!proj?.consolidado;   // títulos "do grupo" na visão consolidada
  const [meta, setMeta] = useState('');
  const [bt, setBt] = useState(null);
  const [btErro, setBtErro] = useState('');

  const vetor = resultado.simulado;
  const p10 = resultado.percentil(10), p50 = resultado.percentil(50), p90 = resultado.percentil(90);

  // histograma em eixo NUMÉRICO (senão as ReferenceLine de P10/P50/P90/zero/meta não acham posição).
  // A escala vai do P0,5 ao P99,5: a cauda longa (poucos cenários extremos) esticava o eixo
  // e espremia o miolo; os extremos entram na barra da ponta.
  const { bins, dominio } = useMemo(() => {
    if (!vetor.length) return { bins: [], dominio: [0, 1] };
    const ord = [...vetor].sort((a, b) => a - b);
    const q = (f) => ord[Math.min(ord.length - 1, Math.floor(f * ord.length))];
    const min = q(0.005), max = q(0.995);
    const n = 28, w = (max - min) / n || 1;
    const acc = Array.from({ length: n }, (_, i) => ({ x: min + w * (i + 0.5), x0: min + w * i, x1: min + w * (i + 1), n: 0 }));
    ord.forEach((v) => { const i = Math.min(n - 1, Math.max(0, Math.floor((v - min) / w))); acc[i].n += 1; });
    return { bins: acc, dominio: [min, max] };
  }, [vetor]);
  const metaNum = parseBR(meta);
  const temMeta = Number.isFinite(metaNum);
  const chance = temMeta ? chanceMeta(metaNum) : null;
  // a meta entra no domínio para a linha ficar dentro do gráfico
  const dominioX = temMeta ? [Math.min(dominio[0], metaNum), Math.max(dominio[1], metaNum)] : dominio;
  const temPrejuizoNoEixo = dominioX[0] < 0;

  // contribuição à variância do resultado: as duas naturezas somam variância
  // (categorias tratadas como independentes)
  const ranking = useMemo(() => {
    const itens = [];
    [['despesa', 'Despesa'], ['receita', 'Receita']].forEach(([nat, rot]) => {
      categoriasDe(proj, nat).forEach((c) => {
        const sig = sigmaAno(c);
        if (sig > 0) itens.push({ chave: `${nat}-${c.codigo}`, natureza: nat, rotulo: rot, codigo: c.codigo, nome: c.nome, var: sig * sig });
      });
    });
    itens.sort((a, b) => b.var - a.var);
    const tot = itens.reduce((s, x) => s + x.var, 0) || 1;
    return itens.slice(0, 10).map((x) => ({ ...x, share: x.var / tot }));
  }, [proj]);

  useEffect(() => {
    let vivo = true;   // troca de cliente: resposta antiga é descartada
    setBt(null); setBtErro('');
    axios.get(`${API}/api/orcamento-projecao-backtest/`, {
      params: { cliente_id: clienteId, ...(consolidar ? { consolidar: 1 } : {}) }, headers: headers(), timeout: 240000 })
      .then((r) => { if (vivo) (r.data?.success ? setBt(r.data) : setBtErro(r.data?.erro || 'Falha')); })
      .catch((e) => { if (vivo) setBtErro(e.response?.data?.erro || 'Erro no backtest'); });
    return () => { vivo = false; };
  }, [clienteId, consolidar]);

  // prejuízo: quanto MAIOR, pior. Meta: quanto MAIOR a chance, melhor (classes de risco invertidas)
  const classePrej = probPrejuizo === null ? '' : (probPrejuizo > 0.5 ? 'alto' : (probPrejuizo > 0.2 ? 'medio' : 'baixo'));
  const classeMeta = chance === null ? '' : (chance >= 0.8 ? 'baixo' : (chance >= 0.5 ? 'medio' : 'alto'));
  const btRes = bt?.resultado_total;

  return (
    <div>
      <div className="op-cenarios">
        <div className="op-cenario"><div className="r">Resultado pessimista · P10</div><div className="v">{fmt(p10)}</div></div>
        <div className="op-cenario" style={{ borderColor: '#00205b' }}><div className="r">Resultado provável · P50</div><div className="v">{fmt(p50)}</div></div>
        <div className="op-cenario"><div className="r">Resultado otimista · P90</div><div className="v">{fmt(p90)}</div></div>
      </div>
      <div className="op-sim-lados">
        <span>Receitas P50 <b>{fmt(receitas.percentil(50))}</b></span>
        <span>Despesas P50 <b>{fmt(despesas.percentil(50))}</b></span>
        {probPrejuizo !== null && (
          <span title="Fração dos cenários simulados em que o resultado do ano ficou abaixo de zero">
            Chance de prejuízo: <span className={`op-prob op-prob-sm ${classePrej}`}>{pct0(probPrejuizo)}</span>
          </span>
        )}
      </div>
      {Math.abs(deslocamento) > 0.5 && (
        <div className="small text-muted mb-2">Suas edições deslocaram a distribuição do resultado em {fmt(deslocamento)} (a forma não muda).</div>
      )}
      <div className="op-histo">
        <div className="op-histo-head">
          <div>
            <div className="op-histo-titulo">Resultado do ano{grupo ? ' do grupo' : ''} em {vetor.length.toLocaleString('pt-BR')} cenários simulados</div>
            <div className="op-histo-sub">Cada cenário combina receita e despesa simuladas juntas. Quanto mais alta a barra, mais vezes o ano terminou com esse resultado.</div>
          </div>
          <div className="op-histo-chave">
            <span><i style={{ background: COR_FAIXA }}></i>faixa provável (P10–P90)</span>
            <span><i style={{ background: COR_CAUDA }}></i>fora da faixa ou prejuízo</span>
            {temPrejuizoNoEixo && <span><i className="zona"></i>zona de prejuízo</span>}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={bins} margin={{ top: 30, right: 24, left: 8, bottom: 4 }} barCategoryGap={0}>
            <CartesianGrid stroke="#eef1f6" vertical={false} />
            {temPrejuizoNoEixo && (
              <ReferenceArea x1={dominioX[0]} x2={Math.min(0, dominioX[1])} fill={INK} fillOpacity={0.05} ifOverflow="hidden" />
            )}
            <ReferenceArea x1={p10} x2={p90} fill={COR_FAIXA} fillOpacity={0.04} ifOverflow="hidden" />
            <XAxis dataKey="x" type="number" domain={dominioX} allowDataOverflow tickCount={6}
              tickFormatter={fmtMi} tick={{ fontSize: 12, fill: '#5a6478' }} axisLine={{ stroke: '#d9dfea' }} tickLine={false} />
            <YAxis hide />
            <Tooltip content={<DicaHistograma total={vetor.length} />} cursor={{ fill: 'rgba(0, 32, 91, 0.05)' }} />
            <Bar dataKey="n" shape={<BarraHistograma />} isAnimationActive={false}>
              {bins.map((b) => <Cell key={b.x} fill={(b.x >= 0 && b.x >= p10 && b.x <= p90) ? COR_FAIXA : COR_CAUDA} />)}
            </Bar>
            <ReferenceLine x={p10} stroke={INK} strokeWidth={1.5} strokeDasharray="4 4" label={rotuloRef('P10', p10, 500)} />
            <ReferenceLine x={p50} stroke={COR_FAIXA} strokeWidth={2} label={rotuloRef('P50', p50, 800)} />
            <ReferenceLine x={p90} stroke={INK} strokeWidth={1.5} strokeDasharray="4 4" label={rotuloRef('P90', p90, 500)} />
            {/* divisa prejuízo | lucro: só aparece se o zero estiver dentro do eixo */}
            <ReferenceLine x={0} stroke={ZERO} strokeWidth={2}
              label={{ position: 'insideTopLeft', value: 'Prejuízo | Lucro', fill: ZERO, fontSize: 12, fontWeight: 700 }} />
            {temMeta && (
              <ReferenceLine x={metaNum} stroke={ZERO} strokeWidth={2} strokeDasharray="2 3" ifOverflow="extendDomain"
                label={{ position: 'insideTopRight', value: `Meta ${fmtMi(metaNum)}`, fill: ZERO, fontSize: 12, fontWeight: 700 }} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="op-calc">
        <div className="linha">
          <span className="pa-eyebrow">Meta de resultado no ano</span>
          <input className="pa-input" style={{ width: 180 }} placeholder="ex.: 500.000,00" value={meta} onChange={(e) => setMeta(e.target.value)} />
          {chance !== null && (
            <span className={`op-prob ${classeMeta}`}>{pct0(chance)}</span>
          )}
          {chance !== null && <span className="text-muted small">de chance de bater a meta (resultado ≥ meta nos cenários simulados)</span>}
        </div>
      </div>
      <CenarioPilares pilares={pilaresSimulados || []} composicaoPilar={composicaoPilar} onAbrirCategoria={onAbrirCategoria}
        grupo={grupo} siglas={siglas} />
      <div className="pa-grid pa-grid-6-6">
        <div className="pa-card">
          <div className="pa-card-head"><div className="pa-card-title">Onde está a incerteza</div><div className="pa-card-sub">contribuição à variância do resultado</div></div>
          <div className="pa-card-body">
            {ranking.map((r) => (
              <div key={r.chave} className="d-flex justify-content-between align-items-center py-1 small gap-2">
                <span><span className={`op-natureza ${r.natureza}`}>{r.rotulo}</span>{r.codigo} {r.nome}</span><b>{(r.share * 100).toFixed(0)}%</b>
              </div>
            ))}
          </div>
        </div>
        <div className="pa-card">
          <div className="pa-card-head"><div className="pa-card-title">Validação (backtest){grupo ? ' do grupo' : ''}</div>
            <div className="pa-card-sub">{bt ? `treino até ${bt.corte} · ${bt.meses_avaliados} meses avaliados` : (btErro ? 'não disponível' : 'rodando…')}</div></div>
          <div className="pa-card-body small">
            {btErro && <div className="text-danger">{btErro}</div>}
            {bt && bt.total && (
              <>
                <div className="op-bt-sec">Despesas</div>
                <Linha rotulo="Erro médio do P50 (total)" valor={pctOuTraco(bt.total.mape_p50)} />
                <Linha rotulo='Erro do "repete o ano anterior"' valor={pctOuTraco(bt.total.mape_ano_anterior)} />
                <Linha rotulo="Meses dentro de P10–P90 (alvo ≈ 80%)" valor={pctOuTraco(bt.total.cobertura_p10_p90, 0)} />
                {bt.receitas?.total && (
                  <>
                    <div className="op-bt-sec">Receitas</div>
                    <Linha rotulo="Erro médio do P50 (total)" valor={pctOuTraco(bt.receitas.total.mape_p50)} />
                    <Linha rotulo='Erro do "repete o ano anterior"' valor={pctOuTraco(bt.receitas.total.mape_ano_anterior)} />
                    <Linha rotulo="Meses dentro de P10–P90 (alvo ≈ 80%)" valor={pctOuTraco(bt.receitas.total.cobertura_p10_p90, 0)} />
                  </>
                )}
                {btRes && btRes.por_mes?.length > 0 && (
                  <>
                    <div className="op-bt-sec">Resultado por mês</div>
                    <Linha rotulo="Meses dentro de P10–P90 (alvo ≈ 80%)" valor={pctOuTraco(btRes.cobertura_p10_p90, 0)} />
                    <table className="op-table op-bt-tab">
                      <thead><tr><th>Mês</th><th style={{ textAlign: 'right' }}>Realizado</th><th style={{ textAlign: 'right' }}>P50</th><th>Na faixa?</th></tr></thead>
                      <tbody>
                        {btRes.por_mes.map((m) => (
                          <tr key={m.mes}>
                            <td>{m.mes.slice(5, 7)}/{m.mes.slice(0, 4)}</td>
                            <td style={{ textAlign: 'right' }}>{fmt(m.real)}</td>
                            <td style={{ textAlign: 'right' }}>{fmt(m.p50)}</td>
                            <td>{m.real >= m.p10 && m.real <= m.p90 ? 'sim' : 'não'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="text-muted mt-1">
                      O erro percentual do resultado fica inflado em meses com resultado perto de zero; leia pela tabela.
                    </div>
                  </>
                )}
                <div className="text-muted mt-2">
                  Treino até {bt.corte}; se o erro do P50 for maior que o do "ano anterior", prefira as médias da análise por categoria.
                </div>
                <div className="text-muted mt-2">Cobertura muito abaixo de 80% indica que as categorias não são independentes — a faixa está estreita demais.</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
